import { Link } from '@tanstack/react-router';
import { Calendar, Code2, Brain, BarChart2, BookOpen, ArrowRight, User } from 'lucide-react';

import { useUpcoming } from '@/lib/hooks/useLms';
import type { MyBatch } from '@/lib/api/lms';

const PALETTE = [
  { iconBg: '#fff0e9', iconColor: '#e8660d', pctColor: '#e8660d', barColor: '#e8660d', btnBg: '#e8660d' },
  { iconBg: '#f0eeff', iconColor: '#7c3aed', pctColor: '#7c3aed', barColor: '#7c3aed', btnBg: '#7c3aed' },
  { iconBg: '#eef6ff', iconColor: '#2563eb', pctColor: '#2563eb', barColor: '#2563eb', btnBg: '#2563eb' },
  { iconBg: '#f0fdf4', iconColor: '#16a34a', pctColor: '#16a34a', barColor: '#16a34a', btnBg: '#16a34a' },
];

const ICONS = [Code2, Brain, BarChart2, BookOpen];

function splitName(name: string): { title: string; sub: string } {
  const sep = name.indexOf(' — ');
  if (sep !== -1) return { title: name.slice(0, sep), sub: name.slice(sep + 3) };
  const sep2 = name.indexOf(' - ');
  if (sep2 !== -1) return { title: name.slice(0, sep2), sub: name.slice(sep2 + 3) };
  return { title: name, sub: '' };
}

function fmtSession(iso: string): string {
  const d = new Date(iso);
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  const day = new Date(d); day.setHours(0, 0, 0, 0);
  if (day.getTime() === today.getTime()) return `Today, ${time}`;
  if (day.getTime() === tomorrow.getTime()) return `Tomorrow, ${time}`;
  return `${d.toLocaleDateString('en-US', { weekday: 'short' })}, ${time}`;
}

export function CourseCard({ batch, index }: { batch: MyBatch; index: number }) {
  const { data: upcoming } = useUpcoming(batch.id);
  const nextSession = upcoming?.find((s): s is typeof s & { scheduled_at: string } => !!s.scheduled_at) ?? null;
  const palette = PALETTE[index % PALETTE.length];
  const Icon = ICONS[index % ICONS.length];
  const { title, sub } = splitName(batch.name);
  const pct = Math.round(batch.progress_pct);

  return (
    <Link
      to="/courses/$batchId"
      params={{ batchId: batch.id }}
      className="group flex flex-col rounded-2xl border border-[#e8e4df] bg-white shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-start gap-4 p-5 pb-4">
        <div
          className="flex size-14 shrink-0 items-center justify-center rounded-2xl"
          style={{ backgroundColor: palette.iconBg }}
        >
          <Icon className="size-6" style={{ color: palette.iconColor }} />
        </div>
        <div className="min-w-0 pt-1">
          <h3 className="text-base font-bold text-[#1a1714] leading-tight">{title}</h3>
          {sub && <p className="text-sm text-[#9a9589] mt-0.5">{sub}</p>}
          {batch.trainer_name && (
            <p className="flex items-center gap-1 text-xs text-[#9a9589] mt-1">
              <User className="size-3 shrink-0" />
              {batch.trainer_name}
            </p>
          )}
        </div>
      </div>

      {/* Progress */}
      <div className="px-5 pb-4">
        <div className="flex items-baseline gap-1.5 mb-2">
          <span className="text-3xl font-bold" style={{ color: palette.pctColor }}>{pct}%</span>
          <span className="text-sm text-[#9a9589]">Complete</span>
        </div>
        <div className="h-2 w-full rounded-full bg-[#f0ede9]">
          <div
            className="h-2 rounded-full transition-all duration-500"
            style={{ width: `${pct}%`, backgroundColor: palette.barColor }}
          />
        </div>
      </div>

      <div className="mx-5 border-t border-[#f0ede9]" />

      {/* Footer */}
      <div className="flex items-center justify-between gap-3 p-4 px-5">
        <div className="flex items-center gap-2 min-w-0">
          <Calendar className="size-4 shrink-0 text-[#9a9589]" />
          <div className="min-w-0">
            <p className="text-xs text-[#9a9589]">Next Session</p>
            <p className="text-sm font-semibold text-[#1a1714] truncate">
              {nextSession ? fmtSession(nextSession.scheduled_at) : 'No sessions'}
            </p>
          </div>
        </div>
        <div
          className="flex shrink-0 items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold text-white"
          style={{ backgroundColor: palette.btnBg }}
        >
          Enter Batch
          <ArrowRight className="size-4" />
        </div>
      </div>
    </Link>
  );
}
